import useAuthState, { Tenant } from '@/states/auth.state';
// import useProcessesState from '@/states/processes.state';
import useTenantState from '@/states/tenant.state';
import { env } from '@/utils/constants/env';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/utils/lib/utils';
import { getInitials } from '@/utils/functions/getInitials';

// Utility function to update favicon
const updateFavicon = (faviconUrl: string) => {
  // Remove existing favicons
  const existingFavicons = document.querySelectorAll("link[rel*='icon']");
  existingFavicons.forEach(favicon => favicon.remove());

  // Add new favicon
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = faviconUrl;
  document.head.appendChild(link);
};

export default function TenantList() {
  const { tenants, isSuperAdmin } = useAuthState();
  const { currentTenant, setCurrentTenant } = useTenantState();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const activeTenant = tenants?.find(t => t.slug === currentTenant?.slug);

  // Update favicon when tenant changes
  useEffect(() => {
    if (activeTenant?.faviconPath) {
      const faviconUrl = activeTenant.faviconPath.startsWith('http')
        ? activeTenant.faviconPath
        : activeTenant.faviconPath.includes('/storage/')
        ? env.apiBaseUrl + activeTenant.faviconPath
        : env.cloudStorage + activeTenant.faviconPath;
      updateFavicon(faviconUrl);
    }
  }, [activeTenant?.faviconPath]);

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton className="outline-none focus:outline-none ring-0 focus:ring-0 w-full">
            <div className="flex items-center justify-start select-none cursor-pointer gap-2 px-4 py-2 overflow-hidden">
              <div className="bg-gray-200 text-primary h-7 w-7 aspect-square cursor-pointer flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
                {activeTenant?.name && (
                  <span className="text-[10px] font-semibold">{getInitials(activeTenant.name)}</span>
                )}
              </div>
              <div className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
                <span className="font-medium text-sm truncate w-full text-left">{currentTenant?.name}</span>
                {isSuperAdmin && <span className="text-[10px] text-blue-600 font-semibold">Super Admin</span>}
              </div>
              <ChevronDown size={14} className="flex-shrink-0" />
            </div>
          </PopoverButton>

          <PopoverPanel
            {...({ transition: true } as any)}
            anchor="bottom start"
            className="w-[300px] origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 z-[51] [--anchor-gap:8px]"
          >
            <Command>
              <CommandInput placeholder="Cari tenant..." />
              <CommandList>
                <CommandEmpty>Tenant tidak ditemukan.</CommandEmpty>
                <CommandGroup heading="Tenants">
                  {isSuperAdmin && (
                    <div className="px-2 py-1.5 text-xs text-blue-700 bg-blue-50 rounded-sm mb-1 mx-1 border border-blue-200">
                      <span className="font-semibold">Akses ke semua workspaces</span>
                    </div>
                  )}
                  {tenants?.map(tenant => {
                    const isSelected = currentTenant?.slug === tenant.slug;

                    return (
                      <CommandItem
                        key={tenant.id}
                        value={tenant.name}
                        onSelect={() => {
                          setCurrentTenant(tenant);
                          setTimeout(() => {
                            queryClient.clear();
                            navigate(`/${tenant.slug}`);
                            close();
                          }, 50);
                        }}
                        className="cursor-pointer"
                      >
                        <div className="bg-gray-200 text-primary h-6 w-6 mr-2 aspect-square flex items-center justify-center rounded-full overflow-hidden shrink-0">
                          <span className="text-[10px] font-semibold">{getInitials(tenant.name)}</span>
                        </div>
                        <span className="flex-1 truncate">{tenant.name}</span>
                        {isSelected && <Check className="ml-auto h-4 w-4 opacity-100" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
